import { Box, Grid, GridItem, Image, useTheme, VStack } from '@chakra-ui/react';

import { formatCurrency } from 'utils';

import { GiftCardInterface } from 'types';

import { Text } from 'ui';
import { CardSelect } from 'ui/CardsContent';

interface PaymentGiftCardItemProps {
  card: GiftCardInterface;
  onSelectCard: () => void;
  isActive: boolean;
  applied?: number;
}

const PaymentGiftCardItem = ({
  card,
  onSelectCard,
  isActive,
  applied = 0
}: PaymentGiftCardItemProps) => {
  const {
    colors: {
      palette: {
        components: { giftCardList }
      }
    }
  } = useTheme();

  return (
    <Grid
      px={{ base: '16px', sm: '30px' }}
      py={{ base: '12px', sm: '14px' }}
      templateColumns={{ base: 'repeat(3, 1fr)', sm: 'repeat(12, 1fr)' }}
      templateRows={{ base: '2', sm: '1' }}
      columnGap={{ base: 0, sm: '10px' }}
      backgroundColor={giftCardList.cardBg}
      borderRadius='6px'
      alignItems='center'
      h='92px'
      w='full'
    >
      <GridItem colSpan={1} rowSpan={{ base: 2, sm: 1 }}>
        <CardSelect
          onClick={onSelectCard}
          isActive={isActive}
          bg={giftCardList.buttonBg}
          activeBg={giftCardList.buttonActiveBg}
          activeBorder={giftCardList.buttonActiveBorderColor}
          activeColor={giftCardList.buttonActiveColor}
        />
      </GridItem>

      <GridItem
        colSpan={{ base: 0, md: 2 }}
        pl={{ base: '11px', sm: '21px', lg: '41px' }}
        display={{ base: 'none', md: 'block' }}
      >
        <Image
          boxShadow={`0 2px 3px ${giftCardList.boxShadowColor}`}
          src={card.img}
          alt='Gift Card'
          width='93px'
          height='64px'
        />
      </GridItem>
      <GridItem colSpan={2}>
        <VStack
          w='full'
          gap={0}
          alignItems={{ base: 'center', sm: 'flex-start' }}
          flexDirection={{ base: 'row-reverse', sm: 'column' }}
        >
          <Text
            fontSize={{ base: 24, md: 32 }}
            lineHeight={{ base: 'auto', md: '43px' }}
            fontWeight='semibold'
            letterSpacing={2}
            color={giftCardList.cardNumberColor}
            width={{ base: 'auto', lg: '50%' }}
            whiteSpace='nowrap'
            textAlign='right'
          >
            x{card.serial.slice(3)}
          </Text>
          <Text
            color={giftCardList.cardNumberColor}
            fontSize={{ base: '10px', sm: '12px', lg: '15px' }}
            textTransform='uppercase'
            whiteSpace='nowrap'
            pr={{ base: 3, sm: 0 }}
            pt={2}
          >
            Gift Card
          </Text>
        </VStack>
      </GridItem>

      <GridItem
        colStart={{ base: isActive ? 1 : 0, sm: 5, md: 7 }}
        colSpan={{ base: 1, sm: 5, md: 4 }}
        alignItems='center'
        textAlign={{ base: 'left', sm: 'right' }}
      >
        {Boolean(applied) && (
          <Box
            bg={giftCardList.appliedBg}
            px={{ base: '6px', sm: '15px' }}
            py='6px'
            borderRadius='12px'
            display='inline-block'
            width={{ base: '100%', sm: 'auto' }}
          >
            <Text
              color={giftCardList.appliedColor}
              textTransform='uppercase'
              fontSize={{ base: '11px', sm: '16px', md: '19px', lg: '21px' }}
              textAlign={{ base: 'center', md: 'left' }}
            >
              {`${formatCurrency(applied)} applied`}
            </Text>
          </Box>
        )}
      </GridItem>

      <GridItem
        colStart={{ base: 3, sm: 10, md: 11 }}
        colSpan={{ base: 1, sm: 3, md: 2 }}
      >
        <Text
          fontSize={{ base: '18px', sm: '20px', md: '22px', lg: '29px' }}
          color={giftCardList.amount}
          textTransform='uppercase'
          textAlign='right'
        >
          {formatCurrency(
            (card.amt_balance < 0 ? 0 : card.amt_balance) - applied
          )}
        </Text>

        {Boolean(applied) && (
          <Text
            color={giftCardList.amount}
            fontSize={{ base: '11px', md: '12px', lg: '15px' }}
            textTransform='uppercase'
            textAlign='right'
          >
            REMAINING
          </Text>
        )}
      </GridItem>
    </Grid>
  );
};

export default PaymentGiftCardItem;
